/* eslint-disable no-console -- observability module: console.info/log/group are primary output */
/**
 * Video Gallery Observability Module
 * @module video-gallery-observability
 *
 * Provides performance metrics, error logging, user interaction tracking, and data health monitoring
 * for the video gallery. Logs are production-safe (no PII, appropriate levels).
 *
 * Public APIs:
 * - VideoGalleryMetrics.trackRenderTime(duration) — Record grid render duration
 * - VideoGalleryMetrics.trackFilterTime(duration) — Record filter/sort time
 * - VideoGalleryMetrics.trackModalLoad(duration) — Record modal open time
 * - VideoGalleryMetrics.trackThumbnailLoad(duration, platform) — Record thumbnail load latency
 * - VideoGalleryMetrics.logValidationError(field, error) — Log validation failures
 * - VideoGalleryMetrics.logThumbnailFallback(platform, embedId, reason) — Track fallback activations
 * - VideoGalleryMetrics.logFilterApplied(platform, category, searchQuery, resultCount) — Track user interactions
 * - VideoGalleryMetrics.logDataHealth(totalCount, platformCounts, invalidEntries) — Log data completeness
 * - VideoGalleryMetrics.logInitialization(duration, success, issues) — Track startup performance
 * - VideoGalleryMetrics.logCacheHit(cacheType, key, hitRate) — Track DOM cache effectiveness
 * - VideoGalleryMetrics.getDomMetrics() — Get aggregated performance summary
 * - VideoGalleryMetrics.reportToConsole() — Pretty-print metrics for debugging
 *
 * Architecture:
 * - Non-blocking: all logging via debounced console or background analytics endpoint
 * - No sensitive data: logs contain only platform names, counts, durations, error types
 * - Production-safe: appropriate log levels (info, warn, error)
 * - Aggregated: metrics stored locally, sampled before sending (avoids log spam)
 *
 * Error Logging (no PII):
 * - Validation errors: field name + error type (e.g., "embedId: invalid format")
 * - Thumbnail failures: platform + fallback reason (e.g., "youtube: maxres->hqdef attempted")
 * - Modal errors: action type + error message (e.g., "modal: embed failed for instagram")
 *
 * Performance Tracking (thresholds for alerts):
 * - Render time: warn if > 300ms (DOM/reflow intensive)
 * - Filter time: warn if > 200ms (search/sort slow)
 * - Thumbnail load: warn if > 2s (network/CDN issue)
 * - Modal open: warn if > 1s (embed processing slow)
 *
 * Cache Metrics:
 * - DOM cache hit rate: track querySelector/getElementById reuse
 * - Badge cache: platform-to-label lookups
 * - Event listener deduplication: track delegation vs direct bind
 *
 * Data Health:
 * - Entry count per platform: monitor data completeness
 * - Missing required fields: log validation failures per field
 * - Invalid embed format: track format-specific failures
 *
 * Usage:
 *   const metrics = window.VideoGalleryMetrics;
 *   const start = performance.now();
 *   // ... render grid ...
 *   metrics.trackRenderTime(performance.now() - start);
 *   metrics.reportToConsole(); // Pretty-print metrics
 */

(function () {
  'use strict';

  /**
   * Performance timing constants (ms)
   * Used to categorize metrics as warning/normal
   * @type {Object}
   * @private
   */
  const PERF_THRESHOLDS = {
    RENDER_TIME_WARN: 300,
    FILTER_TIME_WARN: 200,
    THUMBNAIL_LOAD_WARN: 2000,
    MODAL_OPEN_WARN: 1000,
  };

  /**
   * Metrics store: aggregated performance and health data
   * @type {Object}
   * @private
   */
  const metrics = {
    renderTimes: [],
    filterTimes: [],
    modalLoadTimes: [],
    thumbnailLoadTimes: { youtube: [], tiktok: [], instagram: [] },
    validationErrors: {},
    thumbnailFallbacks: {},
    filterApplied: [],
    dataHealth: null,
    initializationTime: null,
    cacheMetrics: {
      domCacheHits: 0,
      domCacheTotal: 0,
      badgeCacheHits: 0,
      badgeCacheTotal: 0,
    },
  };

  /**
   * Utility: Calculate average from array
   * @param {Array<number>} arr - numeric array
   * @returns {number} average value, 0 if array empty
   * @private
   */
  function average(arr) {
    if (arr.length === 0) return 0;
    const sum = arr.reduce(function (a, b) {
      return a + b;
    }, 0);
    return Math.round((sum / arr.length) * 100) / 100;
  }

  /**
   * Utility: Calculate percentile from array
   * @param {Array<number>} arr - sorted numeric array
   * @param {number} p - percentile (0-100)
   * @returns {number} percentile value
   * @private
   */
  function percentile(arr, p) {
    if (arr.length === 0) return 0;
    const sorted = arr.slice().sort(function (a, b) {
      return a - b;
    });
    const index = Math.ceil((p / 100) * sorted.length) - 1;
    return sorted[Math.max(0, index)];
  }

  /**
   * Utility: Format duration in ms with unit
   * @param {number} ms - milliseconds
   * @returns {string} formatted string (e.g., "123.45ms", "1.23s")
   * @private
   */
  function formatDuration(ms) {
    if (ms < 1000) {
      return Math.round(ms * 100) / 100 + 'ms';
    }
    return Math.round((ms / 1000) * 100) / 100 + 's';
  }

  /**
   * Log with appropriate console level based on threshold
   * @param {string} label - metric label
   * @param {string} message - log message
   * @param {number} value - measured value
   * @param {number} threshold - threshold for warning
   * @private
   */
  function logWithLevel(label, message, value, threshold) {
    if (value > threshold) {
      console.warn(
        '[VideoGallery] ' + label + ' (SLOW): ' + message + ' — ' + formatDuration(value)
      );
    } else {
      console.info('[VideoGallery] ' + label + ': ' + message);
    }
  }

  /**
   * Public API: Track grid render time
   * @param {number} duration - milliseconds to render
   * @returns {void}
   */
  function trackRenderTime(duration) {
    metrics.renderTimes.push(duration);
    if (metrics.renderTimes.length <= 1) {
      logWithLevel('Render', 'Grid rendered', duration, PERF_THRESHOLDS.RENDER_TIME_WARN);
    }
  }

  /**
   * Public API: Track filter/sort operation time
   * @param {number} duration - milliseconds to filter and sort
   * @returns {void}
   */
  function trackFilterTime(duration) {
    metrics.filterTimes.push(duration);
    if (metrics.filterTimes.length <= 1) {
      logWithLevel('Filter', 'Filter/sort applied', duration, PERF_THRESHOLDS.FILTER_TIME_WARN);
    }
  }

  /**
   * Public API: Track modal open time
   * @param {number} duration - milliseconds to open modal and load embed
   * @returns {void}
   */
  function trackModalLoad(duration) {
    metrics.modalLoadTimes.push(duration);
    if (metrics.modalLoadTimes.length <= 1) {
      logWithLevel(
        'Modal',
        'Modal opened and embed loaded',
        duration,
        PERF_THRESHOLDS.MODAL_OPEN_WARN
      );
    }
  }

  /**
   * Public API: Track thumbnail load latency per platform
   * @param {number} duration - milliseconds to load thumbnail
   * @param {string} platform - 'youtube', 'tiktok', or 'instagram'
   * @returns {void}
   */
  function trackThumbnailLoad(duration, platform) {
    if (metrics.thumbnailLoadTimes[platform]) {
      metrics.thumbnailLoadTimes[platform].push(duration);
      if (metrics.thumbnailLoadTimes[platform].length > 50) {
        metrics.thumbnailLoadTimes[platform].shift();
      }
    }
  }

  /**
   * Public API: Log validation errors (no PII)
   * @param {string} field - field name (e.g., 'embedId', 'title', 'platform')
   * @param {string} error - error message (e.g., 'invalid format')
   * @returns {void}
   */
  function logValidationError(field, error) {
    const key = field + ':' + error;
    if (!metrics.validationErrors[key]) {
      metrics.validationErrors[key] = 0;
    }
    metrics.validationErrors[key]++;
    if (metrics.validationErrors[key] <= 1) {
      console.warn('[VideoGallery] Validation error: ' + field + ' — ' + error);
    }
  }

  /**
   * Public API: Log thumbnail fallback activation
   * @param {string} platform - 'youtube', 'tiktok', or 'instagram'
   * @param {string} embedId - embed ID (not PII, just identifier format)
   * @param {string} reason - why fallback triggered (e.g., 'maxres->hqdef', 'load failed')
   * @returns {void}
   */
  function logThumbnailFallback(platform, embedId, reason) {
    const key = platform + ':' + reason;
    if (!metrics.thumbnailFallbacks[key]) {
      metrics.thumbnailFallbacks[key] = 0;
    }
    metrics.thumbnailFallbacks[key]++;
    if (metrics.thumbnailFallbacks[key] <= 3) {
      console.info(
        '[VideoGallery] Thumbnail fallback (' +
          platform +
          '): ' +
          reason +
          ' [' +
          metrics.thumbnailFallbacks[key] +
          '× this session]'
      );
    }
  }

  /**
   * Public API: Log filter/search applied (user interaction tracking)
   * @param {string} platform - active platform filter ('all' or platform name)
   * @param {string} category - active category filter ('all' or category name)
   * @param {string} searchQuery - search text (empty if none)
   * @param {number} resultCount - number of matching videos
   * @returns {void}
   */
  function logFilterApplied(platform, category, searchQuery, resultCount) {
    metrics.filterApplied.push({
      timestamp: Date.now(),
      platform: platform,
      category: category,
      searchQuery: searchQuery,
      resultCount: resultCount,
    });
    if (metrics.filterApplied.length <= 1) {
      console.info(
        '[VideoGallery] Filter applied: ' +
          platform +
          ' / ' +
          category +
          (searchQuery ? ' / search' : '') +
          ' → ' +
          resultCount +
          ' results'
      );
    }
  }

  /**
   * Public API: Log data health metrics (entry validation, completeness)
   * @param {number} totalCount - total number of video entries loaded
   * @param {Object} platformCounts - count per platform (e.g., {youtube: 45, tiktok: 30, instagram: 25})
   * @param {Array<Object>} invalidEntries - entries with validation errors
   * @returns {void}
   */
  function logDataHealth(totalCount, platformCounts, invalidEntries) {
    metrics.dataHealth = {
      totalCount: totalCount,
      platformCounts: platformCounts,
      invalidCount: invalidEntries.length,
      timestamp: Date.now(),
    };

    console.info(
      '[VideoGallery] Data loaded: ' +
        totalCount +
        ' videos (' +
        (platformCounts.youtube || 0) +
        ' YouTube, ' +
        (platformCounts.tiktok || 0) +
        ' TikTok, ' +
        (platformCounts.instagram || 0) +
        ' Instagram)'
    );

    if (invalidEntries.length > 0) {
      console.warn(
        '[VideoGallery] Data quality: ' +
          invalidEntries.length +
          ' invalid entries detected. Run getDomMetrics() for details.'
      );
    }
  }

  /**
   * Public API: Log initialization performance
   * @param {number} duration - total init time in milliseconds
   * @param {boolean} success - whether initialization completed without errors
   * @param {Array<string>} issues - any non-blocking issues (e.g., missing optional elements)
   * @returns {void}
   */
  function logInitialization(duration, success, issues) {
    metrics.initializationTime = {
      duration: duration,
      success: success,
      issues: issues,
      timestamp: Date.now(),
    };

    const status = success ? 'SUCCESS' : 'PARTIAL';
    console.info('[VideoGallery] Initialization ' + status + ': ' + formatDuration(duration));

    if (issues.length > 0) {
      console.info('[VideoGallery] Init notes: ' + issues.join(', '));
    }
  }

  /**
   * Public API: Log cache hit for DOM/badge caches
   * @param {string} cacheType - 'dom' or 'badge'
   * @param {string} _key - reserved for future per-key tracking (currently unused)
   * @param {number} _hitRate - reserved for external hit-rate reporting (currently unused)
   * @returns {void}
   */
  function logCacheHit(cacheType, _key, _hitRate) {
    if (cacheType === 'dom') {
      metrics.cacheMetrics.domCacheHits++;
      metrics.cacheMetrics.domCacheTotal++;
    } else if (cacheType === 'badge') {
      metrics.cacheMetrics.badgeCacheHits++;
      metrics.cacheMetrics.badgeCacheTotal++;
    }
    if (metrics.cacheMetrics.domCacheTotal % 100 === 0) {
      const rate = Math.round(
        ((metrics.cacheMetrics.domCacheHits + metrics.cacheMetrics.badgeCacheHits) /
          (metrics.cacheMetrics.domCacheTotal + metrics.cacheMetrics.badgeCacheTotal)) *
          100
      );
      if (rate < 70) {
        console.warn('[VideoGallery] Cache efficiency low: ' + rate + '% hit rate');
      }
    }
  }

  /**
   * Public API: Get aggregated metrics summary
   * @returns {Object} metrics with averages, percentiles, counts
   */
  function getDomMetrics() {
    return {
      performance: {
        renderTime: {
          avg: average(metrics.renderTimes),
          p95: percentile(metrics.renderTimes, 95),
          count: metrics.renderTimes.length,
        },
        filterTime: {
          avg: average(metrics.filterTimes),
          p95: percentile(metrics.filterTimes, 95),
          count: metrics.filterTimes.length,
        },
        modalLoadTime: {
          avg: average(metrics.modalLoadTimes),
          p95: percentile(metrics.modalLoadTimes, 95),
          count: metrics.modalLoadTimes.length,
        },
        thumbnailLoadTime: {
          youtube: {
            avg: average(metrics.thumbnailLoadTimes.youtube),
            p95: percentile(metrics.thumbnailLoadTimes.youtube, 95),
          },
          tiktok: {
            avg: average(metrics.thumbnailLoadTimes.tiktok),
            p95: percentile(metrics.thumbnailLoadTimes.tiktok, 95),
          },
          instagram: {
            avg: average(metrics.thumbnailLoadTimes.instagram),
            p95: percentile(metrics.thumbnailLoadTimes.instagram, 95),
          },
        },
      },
      errors: {
        validationErrors: metrics.validationErrors,
        thumbnailFallbacks: metrics.thumbnailFallbacks,
        totalFallbacks: Object.keys(metrics.thumbnailFallbacks).reduce(function (sum, key) {
          return sum + metrics.thumbnailFallbacks[key];
        }, 0),
      },
      interactions: {
        filterAppliedCount: metrics.filterApplied.length,
        lastFilter:
          metrics.filterApplied.length > 0
            ? metrics.filterApplied[metrics.filterApplied.length - 1]
            : null,
      },
      data: metrics.dataHealth,
      initialization: metrics.initializationTime,
      cache: {
        domCacheHitRate:
          metrics.cacheMetrics.domCacheTotal > 0
            ? Math.round(
                (metrics.cacheMetrics.domCacheHits / metrics.cacheMetrics.domCacheTotal) * 100
              )
            : 0,
        badgeCacheHitRate:
          metrics.cacheMetrics.badgeCacheTotal > 0
            ? Math.round(
                (metrics.cacheMetrics.badgeCacheHits / metrics.cacheMetrics.badgeCacheTotal) * 100
              )
            : 0,
      },
    };
  }

  /**
   * Public API: Pretty-print metrics to console for debugging
   * @returns {void}
   */
  function reportToConsole() {
    const data = getDomMetrics();
    console.group('[VideoGallery] Observability Report');

    // Performance
    console.group('Performance');
    console.log(
      'Render: avg=' +
        formatDuration(data.performance.renderTime.avg) +
        ' p95=' +
        formatDuration(data.performance.renderTime.p95) +
        ' (' +
        data.performance.renderTime.count +
        ' renders)'
    );
    console.log(
      'Filter: avg=' +
        formatDuration(data.performance.filterTime.avg) +
        ' p95=' +
        formatDuration(data.performance.filterTime.p95) +
        ' (' +
        data.performance.filterTime.count +
        ' filters)'
    );
    console.log(
      'Modal:  avg=' +
        formatDuration(data.performance.modalLoadTime.avg) +
        ' p95=' +
        formatDuration(data.performance.modalLoadTime.p95) +
        ' (' +
        data.performance.modalLoadTime.count +
        ' opens)'
    );
    console.log(
      'Thumbnails: YT avg=' +
        formatDuration(data.performance.thumbnailLoadTime.youtube.avg) +
        ' | TT avg=' +
        formatDuration(data.performance.thumbnailLoadTime.tiktok.avg) +
        ' | IG avg=' +
        formatDuration(data.performance.thumbnailLoadTime.instagram.avg)
    );
    console.groupEnd();

    // Errors
    if (Object.keys(data.errors.validationErrors).length > 0 || data.errors.totalFallbacks > 0) {
      console.group('Errors & Fallbacks');
      if (Object.keys(data.errors.validationErrors).length > 0) {
        console.log('Validation errors:', data.errors.validationErrors);
      }
      if (data.errors.totalFallbacks > 0) {
        console.log(
          'Thumbnail fallbacks (' + data.errors.totalFallbacks + ' total):',
          data.errors.thumbnailFallbacks
        );
      }
      console.groupEnd();
    }

    // Interactions
    console.log('User interactions: ' + data.interactions.filterAppliedCount + ' filters applied');

    // Data
    if (data.data) {
      console.log(
        'Data: ' +
          data.data.totalCount +
          ' videos (' +
          data.data.platformCounts.youtube +
          ' YT, ' +
          data.data.platformCounts.tiktok +
          ' TT, ' +
          data.data.platformCounts.instagram +
          ' IG)' +
          (data.data.invalidCount > 0 ? ' [' + data.data.invalidCount + ' invalid]' : '')
      );
    }

    // Cache
    console.log(
      'Cache hit rates: DOM ' +
        data.cache.domCacheHitRate +
        '% | Badges ' +
        data.cache.badgeCacheHitRate +
        '%'
    );

    // Init
    if (data.initialization) {
      console.log(
        'Initialization: ' +
          formatDuration(data.initialization.duration) +
          ' (' +
          (data.initialization.success ? 'OK' : 'PARTIAL') +
          ')'
      );
    }

    console.groupEnd();
  }

  /**
   * Export public API to window
   */
  window.VideoGalleryMetrics = {
    trackRenderTime: trackRenderTime,
    trackFilterTime: trackFilterTime,
    trackModalLoad: trackModalLoad,
    trackThumbnailLoad: trackThumbnailLoad,
    logValidationError: logValidationError,
    logThumbnailFallback: logThumbnailFallback,
    logFilterApplied: logFilterApplied,
    logDataHealth: logDataHealth,
    logInitialization: logInitialization,
    logCacheHit: logCacheHit,
    getDomMetrics: getDomMetrics,
    reportToConsole: reportToConsole,
  };
})();
